'use client';

import { useState } from 'react';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { Badge, Box, Button, Divider, IconButton, Popover, Typography } from '@mui/material';

import { useT } from '@/hooks';
import type { AppNotification } from '@/services/api/notification.api';
import {
  useMarkNotificationsRead,
  useNotifications,
  useUnreadNotificationCount,
} from '@/services/query';
import { NotificationList } from './NotificationList';

/**
 * Header notification bell + panel. Badge count polls independently; the list is
 * fetched only when the panel is open. Reuses the notifications API (Porvenix data
 * only). Renders nothing structural for guests — mounted only in authed header.
 */
export const NotificationBell = () => {
  const { t } = useT();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const { data, isLoading } = useNotifications(20, open);
  const markRead = useMarkNotificationsRead();

  const notifications: AppNotification[] = data?.data ?? [];

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleMarkAll = () => {
    if (unreadCount > 0) markRead.mutate(undefined);
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label={t('notifications.title', { defaultValue: 'Notifications' })}
        sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
      >
        <Badge
          badgeContent={unreadCount}
          max={99}
          color="error"
          overlap="circular"
          sx={{ '& .MuiBadge-badge': { fontWeight: 800, fontSize: '0.62rem' } }}
        >
          {unreadCount > 0 ? (
            <NotificationsActiveIcon sx={{ fontSize: 24 }} />
          ) : (
            <NotificationsNoneIcon sx={{ fontSize: 24 }} />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        elevation={0}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: { xs: 320, sm: 380 },
              maxWidth: '95vw',
              borderRadius: 3,
              // Match MarketCard's flat surface treatment (background.paper,
              // no elevation overlay, explicit border) instead of the
              // default Popover elevation which washed the panel out.
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>
            {t('notifications.title', { defaultValue: 'Notifications' })}
          </Typography>
          <Button
            size="small"
            startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
            onClick={handleMarkAll}
            disabled={unreadCount === 0 || markRead.isPending}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem' }}
          >
            {t('notifications.markAllRead', { defaultValue: 'Mark all read' })}
          </Button>
        </Box>
        <Divider />

        <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
          <NotificationList
            notifications={notifications}
            isLoading={isLoading}
            onNavigate={handleClose}
          />
        </Box>
      </Popover>
    </>
  );
};
